/**
 * Web Speech API 封裝（#24 Phase C2）· STT（SpeechRecognition）+ TTS（SpeechSynthesis）。
 * Chrome/Edge/Safari 支援（webkit prefix）；不支援時 supported=false → UI 退回文字輸入。
 */
import { useCallback, useEffect, useRef, useState } from 'react';

type SR = any;

const getRecognition = (): SR | null => {
  const w = window as any;
  const Ctor = w.SpeechRecognition || w.webkitSpeechRecognition;
  return Ctor ? new Ctor() : null;
};

export interface UseSpeech {
  supported: boolean;
  listening: boolean;
  startListening: (onResult: (text: string) => void, onError?: () => void) => void;
  stopListening: () => void;
  speak: (text: string) => void;
}

export function useSpeech(lang = 'zh-TW'): UseSpeech {
  const [supported] = useState(() => !!getRecognition() && 'speechSynthesis' in window);
  const [listening, setListening] = useState(false);
  const recRef = useRef<SR | null>(null);

  useEffect(() => () => { try { recRef.current?.abort?.(); } catch { /* noop */ } }, []);

  const startListening = useCallback((onResult: (text: string) => void, onError?: () => void) => {
    const rec = getRecognition();
    if (!rec) { onError?.(); return; }
    recRef.current = rec;
    rec.lang = lang;
    rec.continuous = false;       // one-shot 指令
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = (ev: any) => {
      const text = ev.results?.[0]?.[0]?.transcript || '';
      setListening(false);
      onResult(text);
    };
    rec.onerror = () => { setListening(false); onError?.(); };
    rec.onend = () => setListening(false);
    setListening(true);
    try { rec.start(); } catch { setListening(false); onError?.(); }
  }, [lang]);

  const stopListening = useCallback(() => {
    try { recRef.current?.stop?.(); } catch { /* noop */ }
    setListening(false);
  }, []);

  const speak = useCallback((text: string) => {
    if (!('speechSynthesis' in window) || !text) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = lang;
      const voices = window.speechSynthesis.getVoices();
      const v = voices.find((x) => x.lang === lang) || voices.find((x) => x.lang.startsWith(lang.split('-')[0]));
      if (v) u.voice = v;
      window.speechSynthesis.speak(u);
    } catch { /* silent ok: TTS 失敗不 fatal，畫面仍顯文字 */ }
  }, [lang]);

  return { supported, listening, startListening, stopListening, speak };
}
